import { Controller } from 'react-hook-form';
import { Listbox } from '@headlessui/react';
import PropTypes from 'prop-types';
import React from 'react';

import {
    IconChevronDown,
    IconChevronUp
} from '$partials/icons/index.js';

import { TooltipModal } from '$partials/ui/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const singleSelect = ({
    control = null,
    description = null,
    errors = null,
    id = null,
    isDirty = null,
    name = null,
    options = null,
    required = null
}) => {
    return (
        <Controller
            control={control}
            defaultValue=''
            name={id}
            render={
                ({ field: { ref, ...rest } }) => {
                    return (
                        <div>
                            <div className={`form-input form-input--single-select${isDirty ? ' form-input--dirty' : ''}`}>
                                <Listbox { ...rest }>
                                    {({ open, value }) => {
                                        return (
                                            <div className={open ? 'relative z-10' : ''}>
                                                <Listbox.Button
                                                    as='div'
                                                    className='form-input__label'
                                                    ref={ref}>
                                                    <div className='flex items-center gap-5'>
                                                        {value || name} {required && !value ? <sup>*</sup> : ''}

                                                        {(description && name) && (
                                                            <TooltipModal
                                                                description={description}
                                                                heading={name} />
                                                        )}
                                                    </div>

                                                    <div
                                                        aria-hidden='true'
                                                        className='pl-10 round-button flex-shrink-0'>
                                                        {open && (
                                                            <IconChevronUp />
                                                        )}

                                                        {!open && (
                                                            <IconChevronDown />
                                                        )}
                                                    </div>
                                                </Listbox.Button>

                                                <Listbox.Options className='absolute mt-10 bg-white border border-charcoal rounded overflow-hidden w-[calc(100%_+_20px)] -ml-10'>
                                                    <Listbox.Option
                                                        as={React.Fragment}
                                                        className='type-style-4'
                                                        value={null}>
                                                        <li className='overflow-hidden cursor-pointer select-option py-30 pl-20 hover:bg-sand'>
                                                            &mdash;
                                                        </li>
                                                    </Listbox.Option>

                                                    {options.choices.map((option) => (
                                                        <Listbox.Option
                                                            as={React.Fragment}
                                                            className='type-style-4 border-t border-t-charcoal'
                                                            key={option.id}
                                                            value={option.name}>
                                                            <li className={`overflow-hidden cursor-pointer select-option py-30 pl-20 hover:bg-sand${value === option.name ? ' bg-sand' : ''}`}>
                                                                {option.name}
                                                            </li>
                                                        </Listbox.Option>
                                                    ))}
                                                </Listbox.Options>
                                            </div>
                                        );
                                    }}
                                </Listbox>
                            </div>

                            {(errors?.message) && (
                                <p className='type-style-5 mt-10 text-charcoal'>
                                    {errors.message}
                                </p>
                            )}
                        </div>
                    );
                }
            }
            rules={{ required: required ? 'Please complete this field.' : false }} />
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    control: PropTypes.object.isRequired,
    description: PropTypes.string,
    id: PropTypes.string.isRequired,
    isDirty: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.object.isRequired,
    required: PropTypes.bool
};

singleSelect.propTypes = propTypes;

export default singleSelect;
